import { useRef, useState } from 'react'
import Modal from '@components/common/modal'
import PhoneInput from '@components/book-trial-class/phone-input'
import {
  prepareRegistrationFormBody,
  registerUser,
  requestLoginOtp,
} from '@containers/auth/utils'
import { VerifyPhoneOtpContainer } from '@containers/auth/verify-otp'
import { validatePhone } from '@containers/common/validators'
import Router from 'next/router'
import {
  COMPETITIONS_REF,
  SIGNUP_SOURCE_LOCAL_STORAGE_KEY,
} from '@common/constants'
import { AUTH_MODAL_STATES } from '@lib/enums/competitions'
import swal from 'sweetalert'
import { captureException } from '@sentry/node'
import { SUPPORT_PHONE_NUMBER } from '@common/constants'
import ReCAPTCHA from 'react-google-recaptcha'
import { RecaptchaType } from '@lib/types/recaptcha'
import { TEACHER_JOB_PORTAL_LINK } from '@lib/constants/common'
import SelectInput from '@components/common/forms/select'
import { childGradeOptions } from '@lib/enums/registration-form'
import Button from '@components/atoms/button'
import { ROLES } from '@lib/enums/dashboard'
import { getFromLocalStorage } from '@lib/utils/local-storage'
import RatelimitErrorModalDynamic from '@components/auth/ratelimit-error-modal.dynamic'
import { showErrorAlert } from '@common/utils'
import { validateTrialBookingFormData } from '@containers/book-trial-class/validators'
import { useRouter } from 'next/router'
import { t, Trans } from '@lingui/macro'
import { checkSupportedDevice } from '@lib/utils/system-check'
import { X } from 'react-feather'

export type AuthModalProps = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  slug?: string
  redirectionUrl?: string
  nextRegisterRedirect?: string
  handleOtpVerified?: () => Promise<void>
  phone?: string
  isUserTeacher?: boolean
  /** When grade field is shown, a user is created when the form is submitted */
  isGradeFieldShown?: boolean
  ctaText?: 'Register now' | 'Next'
  isForRegistrationForm?: boolean
}

const AuthModal = ({
  isOpen,
  setIsOpen,
  slug,
  redirectionUrl,
  nextRegisterRedirect,
  handleOtpVerified,
  phone = '',
  isUserTeacher = false,
  isForRegistrationForm = false,
  isGradeFieldShown = false,
  ctaText = t`Next` as 'Next',
}: AuthModalProps): JSX.Element => {
  const [formStep, setFormStep] = useState('phone')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [errors, setErrors] = useState(null)
  const { OTP, PHONE } = AUTH_MODAL_STATES
  const [isLoading, setIsLoading] = useState(false)
  const [childGrade, setChildGrade] = useState({
    value: 'select',
    label: t`Select grade/class`,
  })
  const [isRatelimitModalOpen, setIsRatelimitModalOpen] = useState(false)

  const recaptchaRef = useRef<RecaptchaType>(null)
  const signupRecaptchaRef = useRef<RecaptchaType>(null)
  const router = useRouter()

  const showErrorSwal = () => {
    const errorMessageNode = document.createElement('div')
    errorMessageNode.innerHTML = t`We are not able to send an OTP on your phone number. Please contact support on ${`<a href=tel:${SUPPORT_PHONE_NUMBER.value} class="text-orange ml-1">${SUPPORT_PHONE_NUMBER.label}</a>`} or message us on WhatsApp at ${`<a href="https://wa.me/${SUPPORT_PHONE_NUMBER.value}" class="text-orange ml-1">${SUPPORT_PHONE_NUMBER.label}</a>`}. You can also send us an email at ${`<a class="text-orange ml-1" href="mailto:support@codingal.com">support@codingal.com</a>`}.`
    swal({
      icon: 'error',
      title: 'Sorry!',
      content: { element: errorMessageNode },
    })
  }

  const handleRegisterUser = async (recaptchaResponse: string) => {
    setIsLoading(true)
    try {
      const userData = {
        phone: phoneNumber,
        childGrade,
        name: 'Student',
        email: `${phoneNumber}@codingal-student.com`,
        childGender: {
          value: null,
          label: 'Unspecified',
        },
        role: ROLES.STUDENT,
        hasLaptop: checkSupportedDevice().device === 'desktop',
        termsAndConditions: true,
        whatsappEnabled: true,
        signupSource:
          getFromLocalStorage(SIGNUP_SOURCE_LOCAL_STORAGE_KEY) ||
          Router.asPath.split('?')[0],
      }

      const validationErrors = validateTrialBookingFormData(userData)
      if (Object.keys(validationErrors).length) {
        setErrors(validationErrors)
        setIsLoading(false)
        return
      }

      const formBody = {
        ...prepareRegistrationFormBody(userData),
        recaptchaResponse,
      }

      const signupResponse = await registerUser(formBody)
      if (signupResponse.status === 201) {
        // User created
        const query = {
          phone: phoneNumber,
          ...(nextRegisterRedirect && { next: nextRegisterRedirect }),
          ...router.query,
        }
        Router.push({
          pathname: `/competitions/${slug}/register/`,
          query,
        })
      } else if (signupResponse.status === 429) {
        // signup ratelimit reached
        setIsRatelimitModalOpen(true)
        setIsLoading(false)
      } else if (signupResponse.status === 403) {
        // User blocked from creating too many accounts
        showErrorAlert({
          text: t`You have been blocked. Please contact support if you think it is a mistake.`,
        })
        setIsLoading(false)
      } else {
        setIsLoading(false)
        const error = await signupResponse.json()
        setErrors(error)
      }
    } catch (error) {
      showErrorAlert({
        text: t`Something went wrong while creating your account. Please refresh the page and try again!`,
      })
      captureException(error)
      setIsLoading(false)
    }
  }

  const handleNextButton = async (recaptchaResponse: string = null) => {
    setIsLoading(true)
    const errors = validatePhone(phoneNumber)
    if (Object.keys(errors).length) {
      setErrors(errors)
      setIsLoading(false)
      return
    }
    const formBody = {
      phone: `+${phoneNumber}`,
      ...(recaptchaResponse && { recaptchaResponse }),
    }

    try {
      const result = await requestLoginOtp(formBody)
      if (result.status === 200) {
        setIsLoading(false)
        setFormStep(OTP)
      } else if (result.status === 204) {
        setIsLoading(false)
        const query = {
          phone: phoneNumber,
          ...(nextRegisterRedirect && { next: nextRegisterRedirect }),
        }
        if (isUserTeacher) {
          Router.push({
            pathname: '/register/teacher/',
            query: {
              phone: phoneNumber,
              next: TEACHER_JOB_PORTAL_LINK,
            },
          })
        } else if (isForRegistrationForm) {
          Router.push({
            pathname: '/register/details/',
            query: {
              phone: phoneNumber,
            },
          })
        } else if (isGradeFieldShown)
          // Creates a new account with given phone and grade
          await signupRecaptchaRef.current?.execute()
        else {
          Router.push({
            pathname: `/competitions/${slug}/register/`,
            query,
          })
        }
      } else if (result.status === 400) {
        const errors = await result.json()
        setErrors(errors)
        setIsLoading(false)
      } else if (result.status === 429) {
        await recaptchaRef.current.execute()
      } else if (result.status === 406) {
        setErrors({
          nonFieldErrors: [t`Error verifying reCAPTCHA, please try again`],
        })
        setIsLoading(false)
      } else {
        throw new Error(result.statusText)
      }
    } catch (error) {
      showErrorSwal()
      captureException(error)
      setIsLoading(false)
    }
  }

  const handlePhoneInputChange = (value: string) => {
    setPhoneNumber(value)
  }

  const handleCaptcha = async (key: string) => {
    if (!key) return
    recaptchaRef.current.reset()
    await handleNextButton(key)
  }

  const handleSignupReCaptcha = async (key: string) => {
    if (!key) return
    signupRecaptchaRef.current?.reset()
    await handleRegisterUser(key)
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      id="auth-modal"
      isOverflowHidden={false}
    >
      <>
        <div className="flex justify-end">
          <button
            className="focus:outline-none"
            onClick={() => setIsOpen(false)}
          >
            <X className="stroke-1 text-grey-800" />
          </button>
        </div>
        {!phone && formStep === PHONE && (
          <div className="px-10">
            <div className="mb-2 text-4xl leading-tight text-grey lg:text-5xl font-600">
              <p>
                <Trans>Let&apos;s get started</Trans>
              </p>
            </div>
            <div className="mb-4 text-lg leading-tight text-grey font-600">
              <p>
                <Trans>Enter your phone number</Trans>
              </p>
            </div>
            <div className="mt-6">
              <PhoneInput
                value={phoneNumber}
                onChange={handlePhoneInputChange}
                id="phone"
                name="phone"
                required={true}
                autoFocus={true}
                handleEnterKeyPress={() => handleNextButton()}
                placeholder={t`Enter your mobile number`}
              />
              {errors && errors.phone && (
                <div className="my-2 text-xs leading-4 text-red-200 font-400">
                  {errors.phone}
                </div>
              )}
            </div>
            {isGradeFieldShown && (
              <div className="mt-3">
                <SelectInput
                  id="childGrade"
                  value={childGrade}
                  handleSelectChange={(_, item) => setChildGrade(item)}
                  options={childGradeOptions}
                  errorText={errors?.['childGrade']}
                  isSearchable={false}
                  required
                  isInModal
                />
              </div>
            )}
            {errors && errors.grade && (
              <div className="my-2 text-xs leading-4 text-red-200 font-400">
                {errors.grade}
              </div>
            )}
            {errors && errors.nonFieldErrors && (
              <div className="my-2 text-xs leading-4 text-red-200 font-400">
                {errors.nonFieldErrors}
              </div>
            )}
            <div className="flex mt-5 mb-5">
              <Button
                onClick={() => handleNextButton()}
                loading={isLoading}
                disabled={isLoading}
              >
                {ctaText}
              </Button>
              <button
                className="ml-4 text-orange font-600 hover:underline focus:outline-none"
                onClick={() => setIsOpen(false)}
              >
                <Trans>Cancel</Trans>
              </button>
            </div>
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              onChange={handleCaptcha}
            />
            <ReCAPTCHA
              ref={signupRecaptchaRef}
              size="invisible"
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              onChange={handleSignupReCaptcha}
            />
            <RatelimitErrorModalDynamic
              isOpen={isRatelimitModalOpen}
              setIsOpen={setIsRatelimitModalOpen}
            />
          </div>
        )}
        {(phone || formStep === OTP) && (
          <div className="px-5 mb-5">
            <VerifyPhoneOtpContainer
              phone={phone || phoneNumber}
              redirectUrl={redirectionUrl}
              source={COMPETITIONS_REF}
              handleOtpVerified={handleOtpVerified}
            />
          </div>
        )}
      </>
    </Modal>
  )
}

export default AuthModal
